import { Project, ProrataInformation } from '@gameonly/core';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useUser, { isUserSubscribed } from '../authentication/context/useUser';
import CompleteQuestion from '../cnc-reports/CompleteQuestion';
import ProrataConfiguration from '../cnc-reports/ProrataConfiguration';
import { MenuItemTeasing } from '../subscription/SubscriptionTeasing';
import useProjects from './context/useProjects';
import TransformToCNC from './TransformToCNC';

type StepKey =
  | 'COMPLETE_QUESTION'
  | 'PRORATA_CONFIGURATION'
  | 'TRANFORM_TO_CNC';

type Step = {
  label: string;
  next: ((input: string) => StepKey | null) | (() => null);
};

const STEP_TREE: Record<StepKey, Step> = {
  TRANFORM_TO_CNC: {
    label: 'project:duplicate.modal.transform-to-CNC',
    next: () => 'COMPLETE_QUESTION' as StepKey,
  },
  COMPLETE_QUESTION: {
    label: 'cnc-reports:create-modal-local.complete-question',
    next: (isComplete: string) =>
      isComplete === 'complete' ? null : 'PRORATA_CONFIGURATION',
  },
  PRORATA_CONFIGURATION: {
    label: 'cnc-reports:create-modal-internal.prorata-config',
    next: () => null,
  },
};

const DuplicateProject = ({
  project,
  onOpenDuplicate,
  onClose,
  onOpenSubscription
}: {
  project: Project;
  onOpenDuplicate?: () => void;
  onClose?: () => void;
  onOpenSubscription?: (title: string, text: string, link: string) => void;
}) => {
  const [duplicateLoading, setDuplicateLoading] = useState<boolean>(false);
  const { duplicate } = useProjects();
  const { t } = useTranslation(['project', 'common', 'cnc-reports']);
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  if (!isUserSubscribed(user)) {
    return (
      <MenuItemTeasing
        teasingButton={t('common:subscription.duplicateProject.teasingButton')}
        text={t('common:subscription.duplicateProject.modalText')}
        title={t('common:subscription.duplicateProject.modalTitle')}
        link={t('common:subscription.duplicateProject.knowMoreLink')}
        onOpen={() => onOpenSubscription?.(
          t('common:subscription.duplicateProject.modalTitle'),
          t('common:subscription.duplicateProject.modalText'),
          t('common:subscription.duplicateProject.knowMoreLink')
        )}
      />
    );
  }

  const handleDuplicate = async () => {
    if (project.model.type === 'site') {
      onOpenDuplicate?.();
    } else {
      // For game projects, close the menu and duplicate directly
      onClose?.();
      setDuplicateLoading(true);
      const duplicatedProject = await duplicate({
        projectId: project._id.toString(),
      });
      setDuplicateLoading(false);
      if (duplicatedProject) {
        setDuplicateLoading(false);
        enqueueSnackbar('Projet dupliqué', { variant: 'success' });
        navigate(`/project/${duplicatedProject._id}`);
      }
    }
  };

  return (
    <Tooltip
      title={t('project:duplicate.button-tooltip', {
        modelName: project.model.singularName.toLocaleLowerCase(),
      })}
    >
      <MenuItem onClick={handleDuplicate}>
        <ListItemIcon>
          <ContentCopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          {t('project:duplicate.button-label')}{' '}
          {duplicateLoading && (
            <CircularProgress
              size="small"
              variant="indeterminate"
              sx={{
                width: '20px !important',
                height: '20px !important',
                marginLeft: '10px !important',
              }}
            />
          )}
        </ListItemText>
      </MenuItem>
    </Tooltip>
  );
};

const DuplicateProjectModal = ({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['project', 'common', 'cnc-reports']);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const navigate = useNavigate();
  const [isComplete, setIsComplete] = useState<string>('');
  const [isTransformedToCNC, setIsTransformedToCNC] = useState<string>('');
  const [activeStep, setActiveStep] = useState<StepKey>(
    project.isCNC ? 'COMPLETE_QUESTION' : 'TRANFORM_TO_CNC',
  );
  const { loadingDuplicate, duplicate } = useProjects();

  const handleDuplicateWithProrata = async (
    prorataInformation: ProrataInformation,
  ) => {
    try {
      const duplicatedProject = await duplicate({
        projectId: project._id.toString(),
        prorataInformation,
        isTransformedToCNC: project.isCNC ? true : isTransformedToCNC === 'yes',
      });
      if (duplicatedProject) {
        enqueueSnackbar('Projet créé', { variant: 'success' });
        navigate(`/project/${duplicatedProject._id}`);
      }
    } catch (err) {
      enqueueSnackbar(t('common:error.generic'), { variant: 'error' });
    }
  };

  const handleDuplicateWithoutProrata = async () => {
    try {
      const duplicatedProject = await duplicate({
        projectId: project._id.toString(),
        isTransformedToCNC: project.isCNC ? true : isTransformedToCNC === 'yes',
      });
      if (duplicatedProject) {
        enqueueSnackbar('Projet créé', { variant: 'success' });
        navigate(`/project/${duplicatedProject._id}`);
      }
    } catch {
      enqueueSnackbar(t('common:error.generic'), { variant: 'error' });
    }
  };

  const handleNext = () => {
    if (activeStep) {
      const step = STEP_TREE[activeStep];
      const nextStep = step.next(isComplete);
      if (nextStep) {
        setActiveStep(nextStep);
      } else {
        if (isComplete) {
          handleDuplicateWithoutProrata();
        }
      }
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('project:duplicate.modal.title', {
          modelName: project.model.singularName.toLocaleLowerCase(),
        })}
      </DialogTitle>
      <DialogContent>
        <p className="hxb">{t(STEP_TREE[activeStep].label)}</p>

        {activeStep === 'TRANFORM_TO_CNC' && (
          <Box mt={2}>
            <TransformToCNC
              isTransformedToCNC={isTransformedToCNC}
              setIsTransformedToCNC={setIsTransformedToCNC}
              onClose={onClose}
              onNext={handleNext}
            />
          </Box>
        )}

        {activeStep === 'COMPLETE_QUESTION' && (
          <Box mt={2}>
            <CompleteQuestion
              subscribed={isUserSubscribed(user)}
              isComplete={isComplete}
              setIsComplete={setIsComplete}
              onClose={onClose}
              onNext={handleNext}
              loading={loadingDuplicate}
              onNextLabel={
                isComplete === 'complete'
                  ? t('common:validate')
                  : t('common:next')
              }
            />
          </Box>
        )}

        {activeStep === 'PRORATA_CONFIGURATION' && (
          <Box mt={2}>
            <ProrataConfiguration
              onClose={onClose}
              onSave={handleDuplicateWithProrata}
              loading={loadingDuplicate}
              project={project}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { DuplicateProjectModal };
export default DuplicateProject;
